<script setup lang="ts">
import type { CreditContactValues } from '~/composables/credit';

import { cn } from '@/utils';
import {
	useCreditContactForm,
	useCreditEvents,
	useCreditLeadSubmit,
	useCreditSimulator
} from '~/composables/credit';

import { InputPhone } from '~/components/InputPhone';

import IconMaterialSymbolsLightLockOutline from '~icons/material-symbols-light/lock-outline';

import { creditSimulatorButtonVariants, creditSimulatorFieldVariants } from '.';
import CreditSimulatorProgress from './CreditSimulatorProgress.vue';

type ContactField = keyof CreditContactValues | 'cpf';

const simulator = useCreditSimulator();
const creditEvents = useCreditEvents();
const { isSending, hasSendError, send } = useCreditLeadSubmit();
const hasCpfError = simulator.hasCpfError;
const form = useCreditContactForm();
const { name, phone, email, privacyAccepted, errors } = form;

const heading = useTemplateRef<HTMLElement>('heading');
const fieldRefs: Record<ContactField, Ref<{ focus: () => void } | null>> = {
	name: useTemplateRef('nameField'),
	phone: useTemplateRef('phoneField'),
	cpf: useTemplateRef('cpfField'),
	email: useTemplateRef('emailField'),
	privacyAccepted: useTemplateRef('privacyField')
};
// Same order as on screen; the CPF is validated by the simulator, not the form.
const FIELD_ORDER: ContactField[] = [
	'name',
	'phone',
	'cpf',
	'email',
	'privacyAccepted'
];

defineExpose({ focusHeading: () => heading.value?.focus() });

function hasError(field: keyof CreditContactValues): boolean {
	return Boolean(errors.value[field]);
}

function isInvalid(field: ContactField): boolean {
	return field === 'cpf' ? !simulator.isCpfValid.value : hasError(field);
}

function handleCpfInput(event: Event) {
	simulator.typeCpf((event.target as HTMLInputElement).value);
}

async function handleSubmit() {
	if (isSending.value) return;
	const { valid } = await form.validate();
	if (!valid || !simulator.isCpfValid.value) {
		simulator.flagSubmitAttempt();
		await nextTick();
		const firstInvalid = FIELD_ORDER.find(isInvalid);
		if (firstInvalid) fieldRefs[firstInvalid].value?.focus();
		return;
	}
	const contact = { name: name.value, phone: phone.value, email: email.value };
	if (!(await send(contact))) return;
	creditEvents.trackCompleted(contact);
	simulator.submit();
}
</script>

<template>
	<form class="flex flex-col gap-4.5" novalidate @submit.prevent="handleSubmit">
		<CreditSimulatorProgress
			step-label="Passo 3 de 3"
			detail="Último passo"
			progress-class="w-full"
			:current-step="3"
		/>
		<div>
			<div
				ref="heading"
				tabindex="-1"
				class="text-[20px] font-medium outline-none"
			>
				Para onde enviamos suas propostas?
			</div>
			<div class="mt-1 text-[14px] text-[#A3A3A3]">
				Um especialista Pilar fala com você pelo WhatsApp.
			</div>
		</div>
		<div class="flex flex-col gap-1.5">
			<label for="l-nome" class="text-[14px] font-medium">Nome completo</label>
			<input
				id="l-nome"
				ref="nameField"
				v-model="name"
				v-bind="form.nameAttrs.value"
				type="text"
				autocomplete="name"
				placeholder="Como podemos te chamar?"
				:aria-invalid="hasError('name')"
				:aria-describedby="hasError('name') ? 'l-nome-error' : undefined"
				:class="
					creditSimulatorFieldVariants({
						variant: 'text',
						invalid: hasError('name')
					})
				"
			/>
			<div
				v-if="hasError('name')"
				id="l-nome-error"
				class="text-[13px] font-medium text-[#FF8A7A]"
			>
				{{ errors.name }}
			</div>
		</div>
		<div class="flex flex-col gap-1.5">
			<label for="l-zap" class="text-[14px] font-medium">
				WhatsApp <span class="text-[#FF8A7A]">*</span>
			</label>
			<InputPhone
				ref="phoneField"
				v-model="phone"
				v-bind="form.phoneAttrs.value"
				input-id="l-zap"
				name="phone"
				autocomplete="tel"
				placeholder="(11) 90000-0000"
				:error="hasError('phone')"
				:wrapper-class="
					cn(
						'rounded-xs border-[#3A3A3A] bg-black',
						hasError('phone') && 'border-2 border-[#FF8A7A]'
					)
				"
				select-trigger-class="h-[50px] bg-black"
				select-arrow-class="text-[#FAFAFA]"
				:select-item-classes="{
					wrapper: '',
					flag: '',
					label: 'text-[#FAFAFA]'
				}"
				input-class="h-[50px] bg-black px-3.5 text-[16px] text-[#FAFAFA] placeholder:text-[#6E6E6E]"
			/>
			<div
				v-if="hasError('phone')"
				id="l-zap-error"
				class="text-[13px] font-medium text-[#FF8A7A]"
			>
				{{ errors.phone }}
			</div>
		</div>
		<div class="flex flex-col gap-1.5">
			<label for="l-cpf" class="text-[14px] font-medium"> CPF </label>
			<input
				id="l-cpf"
				ref="cpfField"
				type="text"
				inputmode="numeric"
				autocomplete="off"
				:aria-invalid="hasCpfError"
				aria-describedby="l-cpf-help"
				placeholder="000.000.000-00"
				:value="simulator.state.value.cpf"
				:class="
					cn(
						creditSimulatorFieldVariants({
							variant: 'text',
							invalid: hasCpfError
						}),
						'tracking-[0.5px]'
					)
				"
				@input="handleCpfInput"
			/>
			<div
				v-if="hasCpfError"
				id="l-cpf-help"
				role="alert"
				class="text-[13px] font-medium text-[#FF8A7A]"
			>
				Informe um CPF válido para continuar.
			</div>
			<div v-else id="l-cpf-help" class="text-[12px] text-[#8A8A8A]">
				Opcional. Ajuda os bancos a analisarem seu crédito e enviarem propostas
				reais.
			</div>
		</div>
		<div class="flex flex-col gap-1.5">
			<label for="l-mail" class="text-[14px] font-medium">E-mail</label>
			<input
				id="l-mail"
				ref="emailField"
				v-model="email"
				v-bind="form.emailAttrs.value"
				type="email"
				autocomplete="email"
				placeholder="voce@email.com"
				:aria-invalid="hasError('email')"
				:aria-describedby="hasError('email') ? 'l-mail-error' : undefined"
				:class="
					creditSimulatorFieldVariants({
						variant: 'text',
						invalid: hasError('email')
					})
				"
			/>
			<div
				v-if="hasError('email')"
				id="l-mail-error"
				class="text-[13px] font-medium text-[#FF8A7A]"
			>
				{{ errors.email }}
			</div>
		</div>
		<label
			for="l-lgpd"
			class="flex cursor-pointer items-start gap-2.5 text-[13px] leading-[1.5] text-[#A3A3A3]"
		>
			<input
				id="l-lgpd"
				ref="privacyField"
				v-model="privacyAccepted"
				v-bind="form.privacyAcceptedAttrs.value"
				type="checkbox"
				required
				:aria-invalid="hasError('privacyAccepted')"
				:aria-describedby="
					hasError('privacyAccepted') ? 'l-lgpd-error' : undefined
				"
				:class="
					cn(
						'mt-px mb-0 ml-0 mr-0 size-5 flex-none accent-beige',
						hasError('privacyAccepted') &&
							'outline-2 outline-offset-2 outline-[#FF8A7A]'
					)
				"
			/>
			<span>
				Autorizo a Pilar a usar meus dados para buscar propostas de crédito, a
				compartilhá-los com as instituições parceiras e a consulta do meu CPF em
				birôs de crédito e no SCR do Banco Central, conforme a
				<NuxtLink
					to="/politica-de-privacidade"
					target="_blank"
					class="text-beige underline hover:text-[#F0DDBF]"
				>
					Política de Privacidade</NuxtLink
				>.
			</span>
		</label>
		<div
			v-if="hasError('privacyAccepted')"
			id="l-lgpd-error"
			class="-mt-2 text-[13px] font-medium text-[#FF8A7A]"
		>
			{{ errors.privacyAccepted }}
		</div>
		<div
			v-if="hasSendError"
			role="alert"
			class="text-[13px] font-medium text-[#FF8A7A]"
		>
			Não conseguimos enviar agora. Tente de novo em instantes.
		</div>
		<div class="flex gap-2.5">
			<button
				type="button"
				:class="creditSimulatorButtonVariants({ variant: 'back' })"
				@click="simulator.back()"
			>
				Voltar
			</button>
			<button
				type="submit"
				:class="
					cn(
						creditSimulatorButtonVariants({ variant: 'primary' }),
						'disabled:cursor-wait disabled:opacity-60'
					)
				"
				:disabled="isSending"
			>
				{{ isSending ? 'Enviando…' : 'Receber minhas propostas' }}
			</button>
		</div>
		<div class="flex items-center gap-2 text-[12px] text-[#8A8A8A]">
			<IconMaterialSymbolsLightLockOutline
				class="size-3.5"
				aria-hidden="true"
			/>
			Dados protegidos pela LGPD. Não enviamos spam.
		</div>
	</form>
</template>
